"use client";

import { startTransition, useEffect, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import { trackSEOIntentCaptured } from "@/lib/analytics-seo-bridge";
import { safeSessionGet, safeSessionSet } from "@/lib/browser-storage";
import {
  getSEOIntentConfig,
  getSEOIntentConfigForSource,
  normalizeSEOIntent,
  type SEOIntentConfig,
} from "@/lib/seo-intents";
import { intentReportPlanIdFromPath } from "@/lib/post-kundali-cta";
import { REPORT_BRIDGE_CHECKOUT_PARAM } from "@/lib/report-input-href";
import { isChatFirstSource } from "./KundaliFormCard.actions";
import type { CapturedSEOEntry } from "./KundaliFormCard.parts";

type SEOIntentParams = ReturnType<typeof readSEOIntentParams>;

export function useCapturedSEOEntry(seoEntryOverride: CapturedSEOEntry | null | undefined) {
  const pathname = usePathname();
  // The route's public intent belongs in server HTML as well as hydration.
  // Browser query/session personalization and persistence still run in effects.
  const routeEntry = useMemo(() => dedicatedSurfaceEntry(pathname), [pathname]);
  const [capturedSeoEntry, setCapturedSeoEntry] = useState<CapturedSEOEntry | null>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (seoEntryOverride !== undefined) {
      // A page-supplied override (e.g. the /career-astrology tool) used to
      // BYPASS persistence entirely, so the seeker's dedicated intent never
      // reached sessionStorage and the downstream chat/kundali offers lost it
      // (the 2026-06-09 career_report → ₹0 under-recovery class). Persist it
      // like a captured entry so the intent survives to /chat/[readingId].
      if (seoEntryOverride) persistSEOEntryOverride(seoEntryOverride);
      return;
    }
    const captureIntent = () => captureSEOIntent(setCapturedSeoEntry);
    captureIntent();
    window.addEventListener("seo-intent-refresh", captureIntent);
    return () => window.removeEventListener("seo-intent-refresh", captureIntent);
  }, [seoEntryOverride]);
  return capturedSeoEntry ?? (seoEntryOverride === undefined ? routeEntry : null);
}

/**
 * The entry the form's intent banner shows. A chat-first form hands off to
 * /chat/<id>, where no report checkout opens, so its banner must not promise
 * "your ₹199 report opens right here": it echoes the seeker's question instead.
 * The stored entry (and its plan id) is untouched for analytics and the
 * downstream offer, which resolves the plan from the entry path (C01).
 */
export function formBannerSEOEntry(
  entry: CapturedSEOEntry | null,
  source: string
): CapturedSEOEntry | null {
  if (!entry?.reportPlanId || !isChatFirstSource(source)) return entry;
  return { ...entry, reportPlanId: undefined };
}

function dedicatedSurfaceEntry(path: string | null): CapturedSEOEntry | null {
  const reportPlanId = intentReportPlanIdFromPath(path);
  if (!path || !reportPlanId) return null;
  return { config: getSEOIntentConfigForSource(path), sourcePath: path, reportPlanId };
}

/**
 * Persist a page-supplied SEO entry override into the same sessionStorage keys
 * `captureSEOIntent` writes — but ONLY when the override's surface carries a
 * DEDICATED report intent (career/marriage), resolved by the single shared
 * `intentReportPlanIdFromPath` the downstream offers read (rules 23/24 — one
 * resolver, no drift). Generic tool overrides (nadi, ai-astrologer island)
 * intentionally do NOT persist, so they can never clobber a stored dedicated
 * entry. Returns whether the entry was persisted (pinned by tests).
 * @internal
 */
export function persistSEOEntryOverride(entry: CapturedSEOEntry): boolean {
  try {
    if (!intentReportPlanIdFromPath(entry.sourcePath)) return false;
    persistSEOIntent(
      entry.config,
      entry.sourcePath,
      entry.config.prompt,
      NO_QUERY_PARAMS,
      entry.reportPlanId
    );
    return true;
  } catch {
    return false;
  }
}

const NO_QUERY_PARAMS: SEOIntentParams = {
  intent: undefined,
  source: undefined,
  prompt: undefined,
  topic: undefined,
  auto: undefined,
  nextReport: undefined,
};

function captureSEOIntent(setCapturedSeoEntry: (entry: CapturedSEOEntry) => void) {
  try {
    const params = readSEOIntentParams();
    const intent = normalizeSEOIntent(params.intent || safeSessionGet("seo_entry_intent"));
    if (shouldSkipGenericIntent(intent, params.intent)) {
      // No explicit ?intent= and nothing stored — but the form may be embedded
      // ON a dedicated-report surface itself (e.g. /career-astrology-consultation
      // renders a bare KundaliFormCard). Capture the surface's own intent so the
      // career/marriage signal survives to the downstream offers instead of
      // being dropped (the 2026-07-02 under-recovery class).
      captureDedicatedSurfaceEntry(setCapturedSeoEntry);
      return;
    }

    const config = getSEOIntentConfig(intent);
    const sourcePath =
      params.source || safeSessionGet("seo_entry_path") || document.referrer || "direct";
    const effectivePrompt = params.prompt && params.prompt.trim() ? params.prompt : config.prompt;
    const reportPlanId = resolveReportPlanId(params, config);
    persistSEOIntent(config, sourcePath, effectivePrompt, params, reportPlanId);
    const effectiveConfig = params.prompt ? { ...config, prompt: effectivePrompt } : config;
    startTransition(() =>
      setCapturedSeoEntry({
        config: effectiveConfig,
        sourcePath,
        reportPlanId,
        ...bridgeCheckoutFlag(params, reportPlanId),
      })
    );
    trackCapturedSEOIntent(config, sourcePath, params);
  } catch {
    // Keep the form usable when query parsing or storage is blocked.
  }
}

/** Set only for a collect-inputs bridge arrival that resolved a report plan. */
function bridgeCheckoutFlag(params: SEOIntentParams, reportPlanId: string | undefined) {
  return params.opensCheckout && reportPlanId ? { opensCheckout: true } : {};
}

interface SEOIntentParamValues {
  intent?: string;
  source?: string;
  prompt?: string;
  topic?: string;
  auto?: string;
  nextReport?: string;
  /** open_checkout=1 with a link-borne next_report and origin_placement. */
  opensCheckout?: boolean;
}

function readSEOIntentParams(): SEOIntentParamValues {
  const params = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const getParam = (key: string) => params.get(key) || hashParams.get(key) || undefined;
  return {
    intent: getParam("intent"),
    source: getParam("source"),
    prompt: getParam("prompt") || safeSessionGet("seo_entry_prompt") || undefined,
    topic: getParam("topic") || safeSessionGet("seo_entry_topic") || undefined,
    auto: getParam("auto"),
    nextReport:
      getParam("next_report") ||
      (safeSessionGet("seo_entry_primary_offer") === "report"
        ? safeSessionGet("seo_entry_report_plan_id") || undefined
        : undefined),
    // The bridge flag counts only with a link-borne next_report and placement,
    // the same three params the result's primaryReportArrival reads.
    opensCheckout:
      getParam(REPORT_BRIDGE_CHECKOUT_PARAM) === "1" &&
      Boolean(getParam("origin_placement") && getParam("next_report")),
  };
}

function shouldSkipGenericIntent(intent: string, explicitIntent?: string) {
  return intent === "generic" && !explicitIntent && !safeSessionGet("seo_entry_intent");
}

/**
 * Capture the LIVE surface's dedicated-report intent when nothing more explicit
 * exists. Gated on the same shared resolver the offers read, so only true
 * dedicated surfaces (career/marriage page categories) persist; a generic
 * embed (home, /free-kundali) stays a no-op exactly as before.
 */
function captureDedicatedSurfaceEntry(setCapturedSeoEntry: (entry: CapturedSEOEntry) => void) {
  const entry = dedicatedSurfaceEntry(window.location.pathname);
  if (!entry) return;
  const { config, sourcePath: path, reportPlanId } = entry;
  persistSEOIntent(config, path, config.prompt, NO_QUERY_PARAMS, reportPlanId);
  startTransition(() => setCapturedSeoEntry(entry));
  trackCapturedSEOIntent(config, path, NO_QUERY_PARAMS);
}

/**
 * The SPECIFIC paid report this visitor arrived to buy. The report landing page's
 * `next_report` wins over the intent's generic `reportPlanId` (that generic id is
 * why a Career-Report seeker was shown a Brihat/blank context). Only a report
 * entry — an intent that sells a report, or an explicit `next_report` — resolves
 * a plan; pure-tool visitors get undefined (no report banner).
 * @internal
 */
export function resolveReportPlanId(
  params: SEOIntentParams,
  config: SEOIntentConfig
): string | undefined {
  if (params.nextReport) return params.nextReport;
  if (config.primaryOffer === "report") return config.reportPlanId;
  return undefined;
}

function persistSEOIntent(
  config: SEOIntentConfig,
  sourcePath: string,
  effectivePrompt: string,
  params: SEOIntentParams,
  reportPlanId?: string
) {
  safeSessionSet("seo_entry_intent", config.intent);
  safeSessionSet("seo_entry_path", sourcePath);
  safeSessionSet("seo_entry_page_category", config.pageCategory);
  safeSessionSet("seo_entry_primary_offer", config.primaryOffer);
  // Persist the SPECIFIC report (next_report) over the intent's generic one, so
  // the resolved report plan survives to the downstream chat/kundali offer.
  safeSessionSet("seo_entry_report_plan_id", reportPlanId || config.reportPlanId);
  safeSessionSet("seo_entry_prompt", effectivePrompt);
  if (params.topic) safeSessionSet("seo_entry_topic", params.topic);
  if (params.auto === "1") safeSessionSet("seo_entry_auto_send", "1");
}

function trackCapturedSEOIntent(
  config: SEOIntentConfig,
  sourcePath: string,
  params: SEOIntentParams
) {
  trackSEOIntentCaptured({
    source_path: sourcePath,
    intent: config.intent,
    page_category: config.pageCategory,
    target: "free-kundali",
    ...(params.topic ? { topic_key: params.topic } : {}),
    ...(params.prompt ? { chip_prompt: true } : {}),
  });
}
